import {useMemo} from 'react';
import {hasRightToEdit} from '../../../../../utils/role-to-rights';
import {ResourceType} from '../../../../../@types/resource-type';
import OccurrenceReportDescriptor from '../../../../../@types/occurrence-report-descriptor';
import {DescriptorState} from './descriptor-constants';
import {PermissionCheck} from './descriptor-types';

// User type is provided as parameter, no need to import

/**
 * Custom hook for managing descriptor permissions
 */
export function useDescriptorPermissions(
    descriptor: OccurrenceReportDescriptor,
    user?: any
): PermissionCheck {

    return useMemo((): PermissionCheck => {
        // Check if descriptor exists and has an ID (not a new descriptor)
        const isExistingDescriptor = descriptor.id !== null && descriptor.id !== undefined;

        // Check if user has edit rights
        const hasEditRights = hasRightToEdit({
            objectType: ResourceType.OCCURRENCE_REPORT_DESCRIPTORS,
            object: descriptor
        }, user?.superUser);

        // Check if descriptor is in DRAFT state
        const isDraftState = descriptor.descriptorState === DescriptorState.DRAFT;

        // Can only edit if:
        // 1. It's a new descriptor (no ID), OR
        // 2. User has edit rights AND descriptor is in DRAFT state
        const canEdit = !isExistingDescriptor || (hasEditRights && isDraftState);

        // Can only publish if:
        // 1. Descriptor exists (has ID)
        // 2. User has edit rights
        // 3. Descriptor is in DRAFT state
        const canPublish = isExistingDescriptor && hasEditRights && isDraftState;

        // Determine if fields should be editable
        const isEditable = canEdit;

        return {
            canEdit, canPublish, isEditable
        };
    }, [descriptor, user]);
}

/**
 * Helper to determine if a field should be disabled
 */
export function isFieldDisabled(permissions: PermissionCheck): boolean {
    return !permissions.isEditable;
}

/**
 * Helper to determine if save button should be disabled
 */
export function isSaveDisabled(permissions: PermissionCheck, hasErrors: boolean): boolean {
    return !permissions.canEdit || hasErrors;
}

/**
 * Helper to determine if publish button should be disabled
 */
export function isPublishDisabled(permissions: PermissionCheck): boolean {
    return !permissions.canPublish;
}